import { useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import Button from '@ca/ui/buttons/Button'
import ButtonsGrid from '@ca/ui/buttons/ButtonsFixed/ButtonsGrid'
import { colors } from '@ca/ui/colors'
import ModalBottom from '@ca/ui/components/Modal/variants/ModalBottom'
import ChipButton from '@ca/ui/elements/ChipButton'
import Instructions from '@ca/ui/features/measurement/Instructions'
import Review from '@ca/ui/features/measurement/Review'
import {
  sContainerHeader,
  sContainerOuter,
} from '@ca/ui/features/measurement/Review/styles'
import Manual from '@ca/ui/features/measurement/Review/components/ManualAdjustment'
import type {
  ResultValueType,
  Side,
} from '@ca/ui/features/measurement/Review/types'
import type { PointProps } from '@ca/ui/features/measurement/Points/types'
import VBOX from '@ca/ui/features/measurement/Settings/VBOX'
import Typography from '@ca/ui/typography'
import {
  ExpoOpticalMeasureView,
  getOpticalMeasureSettings,
  resolveOpticalMeasureFramingSettings,
} from '../index'
import type {
  FaceData,
  FaceDataEvent,
  OpticalMeasurePointAdjustmentState,
  OpticalMeasurePointSide,
  OpticalMeasureOverlayDataEvent,
  OpticalMeasureOverlayPoint,
  OpticalMeasureOverlayPointKind,
  OpticalMeasureSettings,
  OpticalMeasureViewHandle,
} from '../index'
import { buildShDebugScenarios } from './internal/debugScenarios'
import { deriveShViewState } from './internal/deriveViewState'
import { isAuthoritativeShResult } from './internal/isAuthoritativeShResult'
import { MeasurementFlowDebugControls } from './internal/MeasurementFlowDebugControls'
import { MeasurementPointOverlay } from './internal/MeasurementPointOverlay'
import { roundShMeasurementResult } from './internal/roundMeasurementResults'
import {
  appendShCaptureLogEntry,
  selectShCaptureLogEntries,
  shCaptureLogConfig,
  shCaptureLogEntry,
  type ShCaptureLogEntry,
} from './internal/shCaptureLog'
import { ShCaptureLogModal } from './internal/ShCaptureLogModal'
import { ShCaptureStatsLine } from './internal/ShCaptureStatsLine'
import { checkShSampleSpread } from './internal/shSampleSpread'
import { checkShVboxRange } from './internal/shVboxRange'
import { useMeasurementFlowSession } from './internal/useMeasurementFlowSession'
import { useMeasurementFramingGuide } from './internal/useMeasurementFramingGuide'
import { useSHCapture } from './internal/useSHCapture'
import {
  MeasurementCloseButton,
  MeasurementFlowScaffold,
} from './MeasurementFlowScaffold'
import {
  defaultOutOfRangeWarning,
  defaultUnstableWarning,
  resolveShMessages,
} from './messages'
import {
  applyShTestingSettings,
  isDefaultShTestingSettings,
  resolveShTestingSettings,
} from './shTestingSettings'
import { ShTestingSettingsModal } from './ShTestingSettingsModal'
import type {
  ShFlowViewState,
  ShMeasurementFlowProps,
  ShMeasurementResult,
  ShTestingSettingsSelection,
} from './types'

const SH_OVERLAY_POINT_KINDS = new Set<OpticalMeasureOverlayPointKind>([
  'left-pupil',
  'right-pupil',
  'left-lens-bottom',
  'right-lens-bottom',
])

const SH_DEBUG_OVERLAY_POINT_KINDS = new Set<OpticalMeasureOverlayPointKind>([
  ...SH_OVERLAY_POINT_KINDS,
  'left-lens-top',
  'right-lens-top',
])

const SIDE_TO_NATIVE = {
  r: 'right',
  l: 'left',
} as const satisfies Record<Side, OpticalMeasurePointSide>

/** The only points manual adjustment moves; pupils and lens tops are context. */
const ADJUSTABLE_KIND_TO_SIDE: Partial<
  Record<OpticalMeasureOverlayPointKind, OpticalMeasurePointSide>
> = {
  'left-lens-bottom': 'left',
  'right-lens-bottom': 'right',
}

/** Per-side manual offset in mm, keyed the way the native commands are. */
type ShOffsets = Record<OpticalMeasurePointSide, number>

const NO_OFFSETS: ShOffsets = { left: 0, right: 0 }

const resultType = (offsetMm: number): ResultValueType =>
  offsetMm === 0 ? 'automatic' : 'manual'

const settingsWithVbox = (
  settings: OpticalMeasureSettings,
  vboxMm: number
): OpticalMeasureSettings => ({
  ...settings,
  sh: {
    ...settings.sh,
    scale: {
      ...settings.sh.scale,
      vboxMm,
    },
  },
})

export function ShMeasurementFlow({
  vboxMm,
  settings,
  messages: messageOverrides,
  isDev = false,
  isDebug = false,
  testingSettings,
  onTestingSettingsChange,
  adjustmentRetries = 0,
  formatOutOfRangeWarning,
  formatUnstableWarning,
  onComplete,
  onCancel,
}: ShMeasurementFlowProps) {
  const messages = resolveShMessages(messageOverrides)
  const [flowSettings, setFlowSettings] = useState(() => {
    const defaults = getOpticalMeasureSettings(false)
    const candidate = settings ?? defaults
    const framing = resolveOpticalMeasureFramingSettings(candidate.framing)
    const initialSettings = framing
      ? candidate
      : {
          ...candidate,
          framing: defaults.framing,
        }

    const settingsWithResolvedVbox = settingsWithVbox(
      initialSettings,
      vboxMm ?? initialSettings.sh.scale.vboxMm
    )
    return isDev && testingSettings
      ? applyShTestingSettings(settingsWithResolvedVbox, testingSettings)
      : settingsWithResolvedVbox
  })
  const resolvedVboxMm = flowSettings.sh.scale.vboxMm
  const framingProfile =
    resolvedVboxMm > 0 ? flowSettings.framing.vbox : flowSettings.framing.normal
  const measureViewRef = useRef<OpticalMeasureViewHandle>(null)
  const session = useMeasurementFlowSession<ShFlowViewState>()
  const [isVboxOpen, setIsVboxOpen] = useState(false)
  const [isRejectedFinalPreviewOn, setIsRejectedFinalPreviewOn] =
    useState(false)
  const [isTestingSettingsOpen, setIsTestingSettingsOpen] = useState(false)
  const [isCaptureLogOpen, setIsCaptureLogOpen] = useState(false)
  // Dev-only bench log. Survives Repeat (the flow is not remounted for a
  // retry), which is exactly the scope a repeat set needs.
  const [captureLog, setCaptureLog] = useState<ShCaptureLogEntry[]>([])
  const [testingSettingsRevision, setTestingSettingsRevision] = useState(0)
  const [calibrationKey, setCalibrationKey] = useState(0)
  const [retryCount, setRetryCount] = useState(0)
  const [isManualProposalOpen, setIsManualProposalOpen] = useState(false)
  const [isManual, setIsManual] = useState(false)
  const [manualSide, setManualSide] = useState<Side>('r')
  const [offsets, setOffsets] = useState<ShOffsets>(NO_OFFSETS)
  // What native has actually applied, which the UI offsets run ahead of while a
  // command is in flight. Kept in a ref so queued deltas read the latest value.
  const appliedOffsetsRef = useRef<ShOffsets>(NO_OFFSETS)
  const adjustmentQueueRef = useRef<Promise<void>>(Promise.resolve())
  // The vbox the running capture was started with — see the restart effect.
  const appliedVboxRef = useRef(resolvedVboxMm)
  const [result, setResult] = useState<ShMeasurementResult | null>(null)
  const [adjustment, setAdjustment] =
    useState<OpticalMeasurePointAdjustmentState>()
  const sh = useSHCapture(
    flowSettings.sh.capture,
    framingProfile,
    flowSettings.sh.gates
  )
  const framing = useMeasurementFramingGuide()

  const liveViewState = deriveShViewState({
    phase: sh.phase,
    progress: sh.progress,
    result,
    adjustment,
  })
  const viewState = isDev
    ? session.debugScenario?.viewState ?? liveViewState
    : liveViewState

  // Pausing SH means switching off native contour detection: with no accepted
  // contours, no candidate reaches the native session, which keeps its accepted
  // samples and captured count (only `cancelSHCapture` / `clearSHCapture`
  // discard those). Detection is async though, so a request already in flight
  // when the pause starts can still land one candidate — hence face data is
  // dropped here too, which both holds the UI at its pre-pause state and defers
  // a raced completion until the modal closes, where the still-active freeze
  // re-delivers the result on the next frame.
  const isCapturePaused =
    session.isInstructionsOpen ||
    isVboxOpen ||
    isTestingSettingsOpen ||
    isCaptureLogOpen

  // Advisory only — the result stays usable and the capture is never rejected
  // on it. Reads the displayed result rather than the live one so it tracks
  // manual adjustment: dialling a point back into range clears the warning.
  // A debug scenario supplies its own lens height, since the warning depends
  // on one and the flow may be running without any.
  const rangeVboxMm =
    (isDev ? session.debugScenario?.vboxMm : undefined) ?? resolvedVboxMm
  const vboxRangeCheck = viewState.result
    ? checkShVboxRange(rangeVboxMm, viewState.result)
    : null

  // How far apart the accepted samples of this one capture landed. Advisory
  // too, and read from the live aggregate rather than the displayed result:
  // manual adjustment moves the number but says nothing about how steadily it
  // was captured, so unlike the range check this one does not clear.
  const sampleSpreadCheck = viewState.result
    ? checkShSampleSpread(sh.nativeState?.aggregate)
    : null

  // One warning slot, and spread wins. An unstable measurement makes the vbox
  // verdict itself untrustworthy — telling the user "this frame is wrong for
  // you" on the strength of a number we know wobbled is the worse message.
  const resolveWarning = () => {
    if (sampleSpreadCheck) {
      return (formatUnstableWarning ?? defaultUnstableWarning)(
        sampleSpreadCheck
      )
    }
    if (vboxRangeCheck) {
      return (formatOutOfRangeWarning ?? defaultOutOfRangeWarning)(
        vboxRangeCheck
      )
    }
    return undefined
  }

  // Native publishes overlay data once per camera frame. Storing it re-renders
  // the whole flow — including the open instructions modal, whose markdown is
  // re-parsed on every render — so at frame rate it starves the modal's
  // open/close animation. The overlay only shows on review, where the
  // instructions button is hidden, so dropping these while paused is free.
  const handleOverlayData = (event: OpticalMeasureOverlayDataEvent) => {
    if (isCapturePaused) return
    session.setOverlayData(event.nativeEvent)
  }

  const clearNativeState = async () => {
    setIsRejectedFinalPreviewOn(false)
    await measureViewRef.current?.clearSHCapture?.()
    await measureViewRef.current?.clearFreeze?.()
  }

  // Debug-only: a rejected final keeps its own draw groups natively, so the
  // frozen frame can be re-rendered with every contour the model produced —
  // including the discarded candidates — instead of only the selected rims.
  const toggleRejectedFinalPreview = async () => {
    const next = !isRejectedFinalPreviewOn
    const result = await measureViewRef.current?.setRejectedFinalPreview?.(next)
    setIsRejectedFinalPreviewOn(result?.ok === true && next)
  }

  const startCapture = async () => {
    setResult(null)
    setAdjustment(undefined)
    await measureViewRef.current?.clearFreeze?.()
    setCalibrationKey((key) => key + 1)
    sh.start()
    const command = await measureViewRef.current?.startSHCapture?.()
    sh.applyNativeState(command?.shCapture)
  }

  useEffect(() => {
    if (!session.cameraReady) return
    void startCapture()
    // The flow is remounted for each request, so capture starts once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.cameraReady])

  const handleFaceData = (incoming: FaceData) => {
    if (isCapturePaused) return

    sh.handleFaceData(incoming)

    const isAuthoritativeResult = isAuthoritativeShResult(
      incoming,
      sh.getNativeSessionId()
    )

    if (!isAuthoritativeResult || !incoming.sh) return

    setResult({ left: incoming.sh.left, right: incoming.sh.right })
    setAdjustment(incoming.shAdjustment)
  }

  // Point adjustment rebuilds the frozen SH result natively and re-emits it, so
  // the new values arrive through `handleFaceData` like any other frozen frame.
  // Commands are queued because `offsetPoint` takes a *relative* delta: two
  // overlapping calls would both diff against the same applied offset.
  const queueAdjustment = (action: () => Promise<void>) => {
    adjustmentQueueRef.current = adjustmentQueueRef.current
      .then(action)
      // A rejected command must not wedge every later adjustment.
      .catch(() => {})
  }

  const handleOffsetChange = (side: Side, offsetMm: number) => {
    const nativeSide = SIDE_TO_NATIVE[side]
    setOffsets((current) => ({ ...current, [nativeSide]: offsetMm }))

    queueAdjustment(async () => {
      const delta = offsetMm - appliedOffsetsRef.current[nativeSide]
      if (delta === 0) return

      const command = await measureViewRef.current?.offsetPoint?.({
        type: 'sh-bottom',
        side: nativeSide,
        unit: 'mm',
        amount: delta,
      })
      if (command?.ok !== true) return

      appliedOffsetsRef.current[nativeSide] = offsetMm
      setAdjustment(command.adjustment)
    })
  }

  const resetOffsets = () => {
    setOffsets(NO_OFFSETS)

    queueAdjustment(async () => {
      const command = await measureViewRef.current?.resetPoint?.({
        type: 'sh-bottom',
      })
      if (command?.ok !== true) return
      // Written here, not at call time, so an offset still queued ahead of this
      // reset cannot leave `applied` describing a point native no longer holds.
      appliedOffsetsRef.current = { ...NO_OFFSETS }
      setAdjustment(command.adjustment)
    })
  }

  const runRestart = async () => {
    // Marked here rather than at the call site: `runRetry` turns a restart away
    // while another action is in flight, and a restart that never ran must not
    // leave the new vbox recorded as the one the capture uses.
    appliedVboxRef.current = resolvedVboxMm
    session.setDebugScenario(null)
    setIsManual(false)
    setManualSide('r')
    setOffsets(NO_OFFSETS)
    sh.clear()
    await clearNativeState()
    // Clearing the freeze makes every queued adjustment fail, but one may
    // already have been in flight before it — so let the queue settle before
    // declaring nothing applied, or the next session diffs against a stale
    // offset and sends a bogus delta.
    await adjustmentQueueRef.current
    appliedOffsetsRef.current = { ...NO_OFFSETS }
    await startCapture()
  }

  // The capture restart itself. Also driven by a vbox or test-settings change,
  // which reruns the measurement for a new reference rather than because the
  // user asked again — so it neither spends nor resets `retryCount`: the
  // manual-adjustment budget is per session, counting every repeat the user
  // pressed regardless of what the capture was reconfigured with in between.
  const restartCapture = () => session.runRetry(runRestart)

  // Counted inside the guarded action rather than at the call site: `runRetry`
  // turns away a press made while a cancel or another retry is in flight, and
  // a retry that never starts must not spend the budget.
  const handleRetry = () =>
    session.runRetry(async () => {
      setRetryCount((count) => count + 1)
      await runRestart()
    })

  // `adjustmentRetries` is which repeat press opens the proposal, and the press
  // being handled is repeat number `retryCount + 1` — so 1 offers manual
  // adjustment straight from the first review. 0 keeps it switched off
  // entirely, so every repeat starts a new capture.
  //
  // A result outside the frame's range skips the retry budget: recapturing
  // cannot fix a measurement the frame itself rules out, so making the user
  // spend retries before they may correct it wastes their time. An unstable
  // capture skips it on the opposite grounds — a repeat is exactly what it
  // needs, and the correction has to stay available if the repeat is no
  // steadier. The 0 switch still wins in both cases: an operator who turned the
  // feature off gets no exception.
  //
  // An adjustment already made keeps it offered too. Without that, correcting
  // an out-of-range result back into range withdraws the affordance that was
  // just used: the repeat button would fall through to a fresh capture and
  // silently discard the correction.
  const hasManualOffsets = offsets.left !== 0 || offsets.right !== 0
  const isManualAdjustmentOffered =
    adjustmentRetries > 0 &&
    (retryCount + 1 >= adjustmentRetries ||
      vboxRangeCheck != null ||
      sampleSpreadCheck != null ||
      hasManualOffsets)

  const handleReviewRepeat = () => {
    if (!isManualAdjustmentOffered) return handleRetry()
    setIsManualProposalOpen(true)
  }

  const handleManualAdjust = () => {
    setIsManualProposalOpen(false)
    setIsManual(true)
  }

  // Tapping a result card is already an explicit choice of side, so it goes
  // straight into adjustment rather than through the proposal — that modal
  // exists to offer adjustment instead of a repeat, a question the tap has
  // answered. Offered on exactly the same terms as the proposal.
  const handleResultPress = (side: Side) => {
    setManualSide(side)
    setIsManual(true)
  }

  // Native already holds every offset the user dialled in, so committing is
  // just dropping back to the review — which now reads the adjusted sides as
  // manual. Finishing the measurement stays the review's own "continue".
  const handleManualOverride = () => setIsManual(false)

  // Leaving manual adjustment discards it: the automatic review has to show the
  // measurement native actually detected, not a half-finished correction.
  const handleManualCancel = () => {
    setIsManual(false)
    resetOffsets()
  }

  // While adjusting, the selected lens-bottom point is the only live target, so
  // it goes danger and everything else recedes to neutral. Back on review, a
  // point the user actually moved stays flagged as modified.
  const resolvePointVariant = (
    point: OpticalMeasureOverlayPoint
  ): PointProps['variant'] => {
    const side = ADJUSTABLE_KIND_TO_SIDE[point.kind]
    if (isManual) {
      return side === SIDE_TO_NATIVE[manualSide] ? 'danger' : 'neutral'
    }
    if (side && offsets[side] !== 0) return 'modified'
    return 'auto'
  }

  const handleManualDecline = () => {
    setIsManualProposalOpen(false)
    void handleRetry()
  }

  const handleVboxChange = (nextVboxMm: number) => {
    setIsVboxOpen(false)
    if (nextVboxMm === resolvedVboxMm) return
    setFlowSettings((current) => settingsWithVbox(current, nextVboxMm))
  }

  // Vbox scales the SH result and swaps the framing profile, so everything
  // sampled under the previous value is void. The restart is driven from an
  // effect rather than from the modal handler so the native view has already
  // been committed with the new settings when the capture starts again.
  useEffect(() => {
    if (!session.cameraReady) return
    if (appliedVboxRef.current === resolvedVboxMm) return
    void restartCapture()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.cameraReady, resolvedVboxMm])

  // Curated test settings affect both the native capture session and the
  // React guidance. Restart only after the new settings have reached the
  // native view so no samples survive from the previous configuration.
  useEffect(() => {
    if (!session.cameraReady || testingSettingsRevision === 0) return
    void restartCapture()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.cameraReady, testingSettingsRevision])

  // One config value feeds the chip count, the entries and the modal, so all
  // three agree on which captures belong to the settings now in force.
  const captureLogConfig = shCaptureLogConfig(
    flowSettings,
    resolveShTestingSettings(flowSettings),
    !isDefaultShTestingSettings(resolveShTestingSettings(flowSettings))
  )
  const activeCaptureLog = selectShCaptureLogEntries(
    captureLog,
    captureLogConfig
  ).active

  // Bench instrumentation: one entry per native session, written the first time
  // a completed capture has both a result and an aggregate. Keyed on session id
  // rather than on the effect firing once, so a manual adjustment — which
  // re-runs this with a changed `result` — cannot overwrite the automatic value
  // the repeat set is measuring. `appendShCaptureLogEntry` returns the same
  // array when the session is already logged, so the per-frame re-runs while
  // the review is open bail out of re-rendering.
  useEffect(() => {
    if (!isDev || sh.phase !== 'complete') return
    const sessionId = sh.nativeState?.sessionId
    const aggregate = sh.nativeState?.aggregate
    if (sessionId == null || !aggregate || !result) return
    setCaptureLog((entries) =>
      appendShCaptureLogEntry(
        entries,
        shCaptureLogEntry(sessionId, result, aggregate, captureLogConfig)
      )
    )
  }, [isDev, sh.phase, sh.nativeState, result, captureLogConfig])

  const applyTestingSettings = (nextSettings: OpticalMeasureSettings) => {
    setIsTestingSettingsOpen(false)
    setFlowSettings(nextSettings)
    setTestingSettingsRevision((revision) => revision + 1)
  }

  const handleTestingSettingsSave = (
    selection: ShTestingSettingsSelection,
    nextSettings: OpticalMeasureSettings
  ) => {
    onTestingSettingsChange?.(
      isDefaultShTestingSettings(selection) ? null : selection
    )
    applyTestingSettings(nextSettings)
  }

  const handleTestingSettingsReset = (nextSettings: OpticalMeasureSettings) => {
    onTestingSettingsChange?.(null)
    applyTestingSettings(nextSettings)
  }

  const handleCancel = () =>
    session.runCancel(async () => {
      sh.stop()
      try {
        await measureViewRef.current?.cancelSHCapture?.()
        await measureViewRef.current?.clearFreeze?.()
      } finally {
        onCancel()
      }
    })

  const handleConfirm = () => {
    if (!viewState.result) return

    const completedResult = roundShMeasurementResult(viewState.result)
    return session.runConfirm(async () => {
      sh.clear()
      await clearNativeState()
      onComplete(completedResult)
    })
  }

  // Camera props stay on live capture state so a debug override never
  // changes the native pipeline behavior.
  const isLiveComplete = sh.phase === 'complete'
  const isReviewReady = viewState.status === 'review'
  const isFailed = viewState.status === 'failed'
  // Available from the first frame — the instructions and the lens height are
  // what the user needs *before* posing, and opening either one pauses the
  // capture anyway. The failed state hands the bottom of the screen over to
  // the retry sheet.
  const isOptionsVisible = !isReviewReady && !isFailed
  const isUsingNonDefaultTestingSettings = !isDefaultShTestingSettings(
    resolveShTestingSettings(flowSettings)
  )
  // Discarded contours are drawn as `isDebugOnly` polylines natively, so the
  // rejected-mask preview only shows them when debug shapes render. This keys
  // off `isDev` alone rather than the preview toggle: changing settings
  // mid-freeze triggers a native rerun, which clears the very draw groups the
  // preview needs. Derived, so a testing-settings update cannot drop it.
  const viewSettings = isDev
    ? {
        ...flowSettings,
        draw: { ...flowSettings.draw, debugShapesEnabled: true },
      }
    : flowSettings

  return (
    <>
      <MeasurementFlowScaffold
        message={viewState.message}
        progress={viewState.progress}
        ovalState={viewState.ovalState}
        ovalFrame={framing.ovalFrame}
        isOvalVisible={framing.isReady && !isLiveComplete}
        isInitializing={!framing.isReady}
        isCloseVisible={!isReviewReady}
        closeLabel={messages.close}
        isCloseDisabled={session.isClosing}
        onClose={() => void handleCancel()}
        closeActions={
          isDev && !isReviewReady ? (
            <ChipButton
              label={isUsingNonDefaultTestingSettings ? 'Modified' : undefined}
              value="Settings"
              color={
                isUsingNonDefaultTestingSettings ? colors.amber[300] : undefined
              }
              disabled={session.isBusy}
              onPress={() => setIsTestingSettingsOpen(true)}
            />
          ) : null
        }
        isInstructionsVisible={isOptionsVisible}
        instructionsLabel={messages.instructions}
        onInstructions={() => session.setIsInstructionsOpen(true)}
        options={
          isOptionsVisible ? (
            <VBOX.Button
              vboxMm={resolvedVboxMm}
              disabled={session.isBusy}
              onPress={() => setIsVboxOpen(true)}
            />
          ) : null
        }
        pointOverlay={
          <MeasurementPointOverlay
            data={session.overlayData}
            isVisible={isLiveComplete}
            visibleKinds={
              isDebug ? SH_DEBUG_OVERLAY_POINT_KINDS : SH_OVERLAY_POINT_KINDS
            }
            resolveVariant={resolvePointVariant}
          />
        }
        overlay={
          isDev ? (
            <MeasurementFlowDebugControls
              scenarios={buildShDebugScenarios()}
              activeKey={session.debugScenario?.key ?? null}
              onSelect={session.setDebugScenario}
            />
          ) : undefined
        }
        isDebug={isDebug}
        camera={
          session.cameraReady ? (
            <ExpoOpticalMeasureView
              ref={measureViewRef}
              style={{ flex: 1 }}
              mode="sh"
              settings={viewSettings}
              calibrationKey={calibrationKey}
              contourDetectionActive={!isLiveComplete && !isCapturePaused}
              measuring={!isLiveComplete}
              // The flow draws its own React overlay, so native drawing stays
              // off — except while previewing a rejected mask, whose contours
              // only exist as native draw groups.
              nativeOverlayEnabled={isDebug && isRejectedFinalPreviewOn}
              onFramingData={framing.handleFramingData}
              onOverlayData={handleOverlayData}
              onFaceData={(event: FaceDataEvent) =>
                handleFaceData(event.nativeEvent)
              }
            />
          ) : (
            <View className="flex-1 bg-black" />
          )
        }
      />

      <ModalBottom
        // No review mask on failure: the whole frozen frame has to stay
        // visible, both for the user to see what was captured and for the
        // debug contour overlay.
        modalProps={{ backdropClose: false }}
        intensity={0}
        open={isFailed}
        center
        cardComponentProps={{ justifyContent: 'flex-end' }}
        hideBottomSpace
        body={
          <View className={sContainerOuter}>
            <View className={sContainerHeader}>
              <Typography variant="XLB" className="text-center">
                {messages.failedTitle}
              </Typography>
              <Typography variant="MD" className="text-center text-desc">
                {messages.failed}
              </Typography>
              {isUsingNonDefaultTestingSettings &&
              sh.nativeState?.rejectionReason ? (
                <Typography
                  variant="XS"
                  className="text-center text-red-700/70"
                >
                  {sh.nativeState.rejectionReason}
                </Typography>
              ) : null}
            </View>
            <View className="flex w-full flex-col gap-4">
              <View className="flex w-full flex-row gap-8">
                <ButtonsGrid>
                  <Button
                    variant="filled"
                    label={messages.retry}
                    disabled={session.isBusy}
                    onPress={() => void handleRetry()}
                  />
                  <Button
                    variant="outlined"
                    label={messages.close}
                    disabled={session.isClosing}
                    onPress={() => void handleCancel()}
                  />
                </ButtonsGrid>
              </View>
              {isDev ? (
                <Button
                  width="full"
                  variant="outlined"
                  label={
                    isRejectedFinalPreviewOn
                      ? 'Hide Rejected Mask'
                      : 'Show Rejected Mask'
                  }
                  disabled={session.isBusy}
                  onPress={() => void toggleRejectedFinalPreview()}
                />
              ) : null}
            </View>
          </View>
        }
      />

      <Review.Modal
        open={isReviewReady && viewState.result != null}
        measurement="sh"
        results={
          viewState.result
            ? {
                r: {
                  value: viewState.result.right,
                  type: resultType(offsets.right),
                  offsetMm: offsets.right,
                  variant:
                    vboxRangeCheck?.isRightOutside ||
                    sampleSpreadCheck?.isRightOutside
                      ? 'attention'
                      : undefined,
                },
                l: {
                  value: viewState.result.left,
                  type: resultType(offsets.left),
                  offsetMm: offsets.left,
                  variant:
                    vboxRangeCheck?.isLeftOutside ||
                    sampleSpreadCheck?.isLeftOutside
                      ? 'attention'
                      : undefined,
                },
              }
            : undefined
        }
        onResultPress={
          isManualAdjustmentOffered ? handleResultPress : undefined
        }
        warning={resolveWarning()}
        offsets={{ r: offsets.right, l: offsets.left }}
        side={manualSide}
        disabled={session.isBusy}
        isManual={isManual}
        onSave={isManual ? handleManualOverride : () => void handleConfirm()}
        onRepeat={() => void handleReviewRepeat()}
        onSideChange={setManualSide}
        onOffsetChange={handleOffsetChange}
        onCancel={handleManualCancel}
        childrenOutside={
          <>
            {/*
              `isDebug`, not `isDev`: a sigma/scaleCv strip over the review
              reads as breakage to anyone not running a bench set, and `isDev`
              is on for any dev-flagged build. The log behind a chip is fine
              there; this is always on screen.
            */}
            {isDebug ? (
              <ShCaptureStatsLine aggregate={sh.nativeState?.aggregate} />
            ) : null}
            <MeasurementCloseButton
              label={messages.close}
              disabled={session.isClosing}
              onPress={() => void handleCancel()}
              before={
                isDev ? (
                  <>
                    <ChipButton
                      value={`Log · ${activeCaptureLog.length}`}
                      disabled={session.isBusy}
                      onPress={() => setIsCaptureLogOpen(true)}
                    />
                    <ChipButton
                      label={
                        isUsingNonDefaultTestingSettings
                          ? 'Modified'
                          : undefined
                      }
                      value="Settings"
                      color={
                        isUsingNonDefaultTestingSettings
                          ? colors.amber[300]
                          : undefined
                      }
                      disabled={session.isBusy}
                      onPress={() => setIsTestingSettingsOpen(true)}
                    />
                  </>
                ) : null
              }
            />
          </>
        }
      />

      <Manual.Proposal
        open={isManualProposalOpen}
        onAdjust={handleManualAdjust}
        onRepeat={handleManualDecline}
        close={() => setIsManualProposalOpen(false)}
      />

      <Instructions.Modal
        open={session.isInstructionsOpen}
        close={() => session.setIsInstructionsOpen(false)}
        measurement="sh"
      />

      <ShCaptureLogModal
        open={isCaptureLogOpen}
        entries={captureLog}
        config={captureLogConfig}
        close={() => setIsCaptureLogOpen(false)}
        onClear={() => setCaptureLog([])}
      />

      <ShTestingSettingsModal
        open={isTestingSettingsOpen}
        settings={flowSettings}
        close={() => setIsTestingSettingsOpen(false)}
        onSave={handleTestingSettingsSave}
        onReset={handleTestingSettingsReset}
      />

      <VBOX.Modal
        open={isVboxOpen}
        vboxMm={resolvedVboxMm}
        onConfirm={handleVboxChange}
        onSkip={() => handleVboxChange(0)}
        // Dismissing keeps the current lens height. Without this the modal
        // falls back to `onSkip`, which the closing animation also fires — so
        // every confirm would be undone by a skip half a second later.
        close={() => setIsVboxOpen(false)}
      />
    </>
  )
}
